import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian.js";
import persian_fa from "react-date-object/locales/persian_fa.js";
import { dayToWeek } from "../../calendar/Calendarr";
import { toFarsiNumber } from "../../../custom/helpers";
import { useGroupApi, imageUrl, toIsoDay, MEAL_LABELS, MAIN_MEALS, SNACKS } from "../groupApi";
import { Sheet, ConfirmSheet } from "./ui";
import AddFoodModal from "./AddFoodModal";

const POLL_MS = 10000;

export default function GroupCalendar({ groupId, canEdit, onRefreshGroup }) {
  const { call, json } = useGroupApi();
  const [anchor, setAnchor] = useState(new Date());
  const [items, setItems] = useState(null); // null = loading
  const [adding, setAdding] = useState(null);      // { date, mealType }
  const [selected, setSelected] = useState(null);  // item being viewed
  const [replacing, setReplacing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const todayRef = useRef(null);
  const scrolledRef = useRef(false);

  const week = useMemo(() => dayToWeek(anchor), [anchor]);
  const days = useMemo(
    () => week.map((d) => ({ iso: toIsoDay(d), label: d.setCalendar(persian).format("dddd DD MMMM") })),
    [week]
  );
  const start = days[0].iso;
  const end = days[6].iso;
  const todayIso = toIsoDay(new DateObject({ date: new Date(), locale: persian_fa }));

  const load = useCallback(async () => {
    try {
      const r = await call(`groups/${groupId}/calendar?start_date=${start}&end_date=${end}`, { method: "GET" });
      setItems(r.items);
      // keep the open detail sheet in sync with what other members just did
      setSelected((cur) => (cur ? r.items.find((i) => i.item_id === cur.item_id) || null : cur));
      if (r.can_edit !== canEdit && onRefreshGroup) onRefreshGroup(); // permission changed under us
    } catch (e) {
      if (e.status === 404 && onRefreshGroup) onRefreshGroup();
    }
  }, [call, groupId, start, end, canEdit, onRefreshGroup]);

  useEffect(() => {
    setItems(null);
  }, [start, end, groupId]);

  // Opening the calendar lands on today's date (once per mount / group), not the top of the week.
  useEffect(() => {
    scrolledRef.current = false;
  }, [groupId]);
  useEffect(() => {
    if (items === null || scrolledRef.current) return;
    scrolledRef.current = true;
    if (todayRef.current) todayRef.current.scrollIntoView({ block: "center" });
  }, [items]);

  useEffect(() => {
    load();
    const id = setInterval(() => !document.hidden && load(), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const bySlot = useMemo(() => {
    const m = {};
    (items || []).forEach((i) => { m[`${i.date}|${i.meal_type}`] = i; });
    return m;
  }, [items]);

  // Translate concurrency/permission errors into friendly feedback + resync.
  const handleError = async (e) => {
    if (e.code === "forbidden") {
      toast.error("دیگه اجازه ویرایش تقویم رو نداری");
      onRefreshGroup && onRefreshGroup();
    } else if (e.code === "slot_taken" || e.code === "version_conflict") {
      toast.warn(e.message);
    } else if (e.code === "item_not_found") {
      toast.info("این وعده قبلا توسط شخص دیگه‌ای حذف شده");
    } else if (e.status === 404 && e.code === "group_not_found") {
      onRefreshGroup && onRefreshGroup();
      return;
    } else {
      toast.error(e.message);
    }
    setSelected(null);
    setReplacing(false);
    await load();
  };

  const addFood = async (recipe, mealType) => {
    setBusy(true);
    try {
      await json(`groups/${groupId}/calendar`, "POST", { recipe_id: recipe.recipe_id, meal_type: mealType, date: adding.date });
      toast.success("به تقویم گروه اضافه شد");
      setAdding(null);
      await load();
    } catch (e) {
      setAdding(null);
      await handleError(e);
    } finally {
      setBusy(false);
    }
  };

  // Custom food: stored as a calendar item only; no FoodPlanner recipe is created (the server also queues
  // the name as a request for the admin). Validation errors keep the sheet open so the text isn't lost.
  const addCustomFood = async (name, mealType) => {
    setBusy(true);
    try {
      await json(`groups/${groupId}/calendar`, "POST", { custom_food_name: name, meal_type: mealType, date: adding.date });
      toast.success("به تقویم گروه اضافه شد؛ بررسی می‌کنیم براش دستور پخت اضافه کنیم");
      setAdding(null);
      await load();
    } catch (e) {
      if (e.status === 400) {
        toast.error(e.message);
      } else {
        setAdding(null);
        await handleError(e);
      }
    } finally {
      setBusy(false);
    }
  };

  const updateItem = async (item, patch, okMsg) => {
    setBusy(true);
    try {
      const updated = await json(`groups/${groupId}/calendar/${item.item_id}`, "PUT", { version: item.version, ...patch });
      toast.success(okMsg);
      setReplacing(false);
      setSelected(updated);
      await load();
    } catch (e) {
      await handleError(e);
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (item) => {
    setBusy(true);
    try {
      await call(`groups/${groupId}/calendar/${item.item_id}`, { method: "DELETE" });
      toast.success("از تقویم گروه حذف شد");
    } catch (e) {
      await handleError(e);
    } finally {
      setBusy(false);
      setConfirmDelete(false);
      setSelected(null);
      await load();
    }
  };

  const shiftWeek = (n) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + n * 7);
    setAnchor(d);
  };

  const rangeLabel = `${days[0].label.split(" ").slice(1).join(" ")} تا ${days[6].label.split(" ").slice(1).join(" ")}`;
  const isEmpty = items !== null && items.length === 0;

  const renderSlot = (day, meal) => {
    const item = bySlot[`${day.iso}|${meal}`];
    if (item) {
      return (
        <button key={meal} type="button" className="grpSlot grpSlot-filled" onClick={() => setSelected(item)}>
          {item.image_path ? <img className="grpSlotImg" src={imageUrl(item.image_path)} alt="" loading="lazy" /> : <span className={`grpSlotImg ${item.is_custom ? "grpSlotImg-custom" : ""}`} aria-hidden="true">{item.is_custom ? "🍽️" : ""}</span>}
          <span>
            <span className="grpSlotMeal yekan">{MEAL_LABELS[meal]}</span>
            <span className="grpSlotName yekan">{item.recipe_name}</span>
          </span>
        </button>
      );
    }
    return (
      <button
        key={meal}
        type="button"
        disabled={!canEdit}
        className={`grpSlot grpSlot-empty ${canEdit ? "grpSlot-can" : ""}`}
        onClick={() => canEdit && setAdding({ date: day.iso, mealType: meal })}
      >
        <span className="yekan caption-1">{canEdit ? `+ ${MEAL_LABELS[meal]}` : MEAL_LABELS[meal]}</span>
      </button>
    );
  };

  const weekNav = (cls) => (
    <div className={cls}>
      <button type="button" className="grpIconBtn" onClick={() => shiftWeek(-1)} aria-label="هفته قبل">
        <span className="icon-iconchevron-right"></span>
      </button>
      <div style={{ textAlign: "center" }}>
        <div className="grpCalRange yekan">{toFarsiNumber(rangeLabel)}</div>
        {!canEdit && <span className="grpViewOnly yekan">فقط مشاهده</span>}
      </div>
      <button type="button" className="grpIconBtn" onClick={() => shiftWeek(1)} aria-label="هفته بعد">
        <span className="icon-iconchevron-left"></span>
      </button>
    </div>
  );

  return (
    <div className="grpPanel">
      {weekNav("grpCalHead")}

      {items === null && (
        <div style={{ padding: 14 }}>
          {[0, 1, 2].map((i) => <div key={i} className="grpSkeleton" style={{ marginBottom: 10, height: 90 }} />)}
        </div>
      )}

      {isEmpty && (
        <div className="grpEmpty">
          <span className="grpEmptyIcon">🍽️</span>
          <div className="grpEmptyTitle yekan">اولین وعده رو به تقویم مشترک اضافه کن</div>
          <div className="yekan caption-1">{canEdit ? "روی یکی از وعده‌ها بزن و غذا انتخاب کن" : "مدیر گروه هنوز وعده‌ای اضافه نکرده"}</div>
        </div>
      )}

      {items !== null && days.map((day) => {
        const snackFilled = SNACKS.filter((s) => bySlot[`${day.iso}|${s}`]);
        const emptySnacks = SNACKS.filter((s) => !bySlot[`${day.iso}|${s}`]);
        return (
          <div key={day.iso} ref={day.iso === todayIso ? todayRef : null} className={`grpDay ${day.iso === todayIso ? "grpDay-today" : ""}`}>
            <div className="grpDayTitle yekan">{toFarsiNumber(day.label)}</div>
            <div className="grpSlots">
              {MAIN_MEALS.map((m) => renderSlot(day, m))}
              {snackFilled.map((m) => renderSlot(day, m))}
            </div>
            {canEdit && emptySnacks.length > 0 && (
              <button type="button" className="grpSnackAdd" onClick={() => setAdding({ date: day.iso, mealType: emptySnacks[0], snack: true })}>
                + میان‌وعده
              </button>
            )}
          </div>
        );
      })}

      {items !== null && weekNav("grpCalHead grpCalFoot")}

      {adding && (
        <AddFoodModal
          title={`افزودن غذا · ${MEAL_LABELS[adding.mealType]}`}
          mealType={adding.mealType}
          showMealPicker={!!adding.snack}
          busy={busy}
          onPick={addFood}
          allowCustom
          customHint="به تقویم گروه"
          onPickCustom={addCustomFood}
          onClose={() => setAdding(null)}
        />
      )}

      {selected && !replacing && !confirmDelete && (
        <Sheet title={selected.recipe_name} onClose={() => setSelected(null)}>
          {selected.image_path && <img className="grpDetailImg" src={imageUrl(selected.image_path)} alt="" />}
          <p className="grey-2 caption-1 yekan" style={{ margin: "0 0 12px" }}>
            {MEAL_LABELS[selected.meal_type]} · {toFarsiNumber(new DateObject({ date: new Date(`${selected.date}T12:00:00`), locale: persian_fa }).setCalendar(persian).format("dddd DD MMMM"))}
            <br />
            اضافه‌شده توسط {selected.added_by}
          </p>
          {selected.is_custom && (
            <p className="grey-2 caption-1 yekan customFoodNote">
              غذای دلخواه · هنوز دستور پختی براش توی فودپلنر نیست؛ درخواست اضافه شدنش ثبت شده.
            </p>
          )}
          <div className="grpRowWrap">
            {selected.recipe_id && <Link to={`/recipe/${selected.recipe_id}`} className="grpBtn grpBtn-light">دستور پخت</Link>}
            {canEdit && (
              <>
                <button type="button" className="grpBtn grpBtn-ghost" onClick={() => setReplacing(true)}>تغییر غذا</button>
                <button type="button" className="grpBtn grpBtn-danger" onClick={() => setConfirmDelete(true)}>حذف</button>
              </>
            )}
          </div>
          {canEdit && (
            <div className="grpSection">
              <div className="grpSectionTitle yekan">جابه‌جایی</div>
              <label className="grpField">
                <span className="yekan">روز</span>
                <select
                  className="grpInput"
                  value={selected.date}
                  disabled={busy}
                  onChange={(e) => updateItem(selected, { date: e.target.value }, "جابه‌جا شد")}
                >
                  {!days.some((d) => d.iso === selected.date) && <option value={selected.date}>{selected.date}</option>}
                  {days.map((d) => <option key={d.iso} value={d.iso}>{toFarsiNumber(d.label)}</option>)}
                </select>
              </label>
              <div className="grpChoice">
                {Object.entries(MEAL_LABELS).map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    disabled={busy}
                    className={`grpChip ${selected.meal_type === k ? "grpChip-active" : ""}`}
                    onClick={() => k !== selected.meal_type && updateItem(selected, { meal_type: k }, "وعده تغییر کرد")}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Sheet>
      )}

      {selected && replacing && (
        <AddFoodModal
          title="تغییر غذا"
          mealType={selected.meal_type}
          busy={busy}
          onPick={(recipe) => updateItem(selected, { recipe_id: recipe.recipe_id }, "غذا عوض شد")}
          allowCustom
          customHint="به تقویم گروه"
          onPickCustom={(name) => updateItem(selected, { custom_food_name: name }, "غذا عوض شد")}
          onClose={() => setReplacing(false)}
        />
      )}

      {selected && confirmDelete && (
        <ConfirmSheet
          title="حذف از تقویم گروه"
          message={`«${selected.recipe_name}» از تقویم مشترک همه اعضا حذف بشه؟`}
          confirmLabel="آره، حذف کن"
          busy={busy}
          onConfirm={() => removeItem(selected)}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
